import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ShipmentStatusBadge } from '@/components/ShipmentStatusBadge'
import { HistoryRowTag } from '@/components/history/HistoryRowTag'
import { formatDate } from '@/lib/format'
import { HAND_DELIVERY_CONTENTS } from '@/lib/history'
import type { HistoryCancelLabel, HistoryHandDelivery, HistoryRenumber, Shipment } from '@/api/types'

export type DeliveryLine =
  | { kind: 'platform'; sequence: number; shipment: Shipment; renumber: HistoryRenumber | null; canRemove: boolean }
  | { kind: 'label'; sequence: number; shipment: Shipment; label: HistoryCancelLabel }
  | { kind: 'hand'; sequence: number; index: number; delivery: HistoryHandDelivery }

interface HistoryDeliveryTableRowProps {
  line: DeliveryLine
  isDisabled: boolean
  onHandChange: (index: number, patch: Partial<HistoryHandDelivery>) => void
  onHandRemove: (index: number) => void
  onLabelKeep: (id: number) => void
  onLabelRemove: (shipment: Shipment) => void
}

export function HistoryDeliveryTableRow({
  line,
  isDisabled,
  onHandChange,
  onHandRemove,
  onLabelKeep,
  onLabelRemove
}: HistoryDeliveryTableRowProps): React.ReactElement {
  if (line.kind === 'platform') {
    const { shipment, renumber, canRemove } = line
    const day = shipment.delivered_at ?? shipment.booked_at ?? shipment.created_at

    return (
      <tr className="border-t border-border/60">
        <td className="px-3 py-2">
          <HistoryRowTag kind={renumber ? 'change' : 'keep'} />
        </td>
        <td className="px-3 py-2 tabular-nums">
          {renumber && (
            <span>
              {renumber.from} → {renumber.to}
            </span>
          )}
          {!renumber && shipment.sequence}
        </td>
        <td className="px-3 py-2">{formatDate(day)}</td>
        <td className="px-3 py-2">
          <ShipmentStatusBadge status={shipment.status} />
          <span className="ml-2 text-xs text-muted-foreground">{shipment.postnord_shipment_id ?? 'ingen fraktsedel'}</span>
        </td>
        <td className="px-3 py-2 text-right">
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none text-destructive hover:text-destructive"
              disabled={isDisabled}
              onClick={() => onLabelRemove(shipment)}
            >
              Ta bort fraktsedeln
            </Button>
          )}
          <Link
            to={`/shipments/${shipment.id}`}
            className="text-xs text-muted-foreground hover:underline"
          >
            rad {shipment.id}
          </Link>
        </td>
      </tr>
    )
  }

  if (line.kind === 'label') {
    const { shipment, label } = line

    return (
      <tr className="border-t border-border/60 bg-rose-50/40">
        <td className="px-3 py-2">
          <HistoryRowTag kind="remove" />
        </td>
        <td className="px-3 py-2 tabular-nums">{shipment.sequence}</td>
        <td className="px-3 py-2">{formatDate(shipment.booked_at ?? shipment.created_at)}</td>
        <td className="px-3 py-2">
          <ShipmentStatusBadge status={shipment.status} />
          <span className="ml-2 text-xs text-muted-foreground">
            {label.postnordId ?? 'ingen fraktsedel'} · fraktsedeln tas bort och avbokas hos PostNord
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-none"
            disabled={isDisabled}
            onClick={() => onLabelKeep(label.id)}
          >
            Behåll fraktsedeln
          </Button>
        </td>
      </tr>
    )
  }

  const { delivery, index } = line

  const handleSequenceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const sequence = Number(event.target.value)

    if (Number.isInteger(sequence) && sequence > 0) onHandChange(index, { sequence })
  }

  return (
    <tr className="border-t border-border/60 bg-emerald-50/40">
      <td className="px-3 py-2">
        <HistoryRowTag kind="add" />
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          min={1}
          value={delivery.sequence}
          disabled={isDisabled}
          onChange={handleSequenceChange}
          aria-label="Leveransnummer"
          className="w-20"
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="date"
          value={delivery.sentOn}
          disabled={isDisabled}
          onChange={(event) => onHandChange(index, { sentOn: event.target.value })}
          aria-label="Skickad den"
          className="w-40"
        />
      </td>
      <td className="px-3 py-2">
        <Select
          value={delivery.contents}
          disabled={isDisabled}
          onValueChange={(contents) => onHandChange(index, { contents })}
        >
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(HAND_DELIVERY_CONTENTS).map(([value, label]) => (
              <SelectItem
                key={value}
                value={value}
              >
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-2 text-xs text-muted-foreground">skickad för hand</span>
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-none text-destructive hover:text-destructive"
          disabled={isDisabled}
          onClick={() => onHandRemove(index)}
        >
          Ta bort raden
        </Button>
      </td>
    </tr>
  )
}
